import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { getAuth } from 'firebase/auth';
import { MessageCircle } from '@styled-icons/feather/MessageCircle';
import { Users } from '@styled-icons/feather/Users';
import { device } from '../../constants/device-sizes';
import { API_URL, MINIMUM_AGE } from '../../constants/string-constants';
import { getSiteName } from '../../hooks/useSiteConfig';
import {
  ModalContent,
  ModernTitle,
  ModernSubtitle,
} from './shared/ModalBase';

const ModalWrapper = styled.div`
  width: 90%;
  max-width: 500px;
  padding: 2rem;
  max-height: 90vh;
  overflow-y: auto;
  @media ${device.tablet} {
    width: 85%;
    max-width: 600px;
    padding: 2.5rem;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin: 2rem 0;
`;

const IntroText = styled.p`
  color: var(--clr-sec);
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 1.5rem 0;
  opacity: 0.9;

  @media ${device.tablet} {
    font-size: 1.2rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
`;

const JoinButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.25rem 3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  min-width: 250px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover:not(:disabled) {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-2px) scale(1.02);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;


const OnlineCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1.5rem 0;
  color: var(--clr-sec);
  font-size: 1rem;
  opacity: 0.8;

  svg {
    width: 1.2rem;
    height: 1.2rem;
    color: var(--clr-purple);
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 10;
`;

const LoadingText = styled.p`
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  color: #ff8a8a;
  font-size: 0.9rem;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff6b6b;
  text-align: center;
`;

const HighlightText = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const AgeDisclaimer = styled.p`
  color: var(--clr-sec);
  font-size: 0.9rem;
  text-align: center;
  margin: 1rem 0;
  opacity: 0.8;

  a {
    color: var(--clr-purple);
    text-decoration: none;
    border-bottom: 1px solid var(--clr-purple);
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const OnboardingModalChatScape = ({ visible, onClose, onRoomSelect }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalOnline, setTotalOnline] = useState(null);

  // Reset loading state when modal is closed
  useEffect(() => {
    if (!visible) {
      setLoading(false);
      setError('');
      setTotalOnline(null);
    }
  }, [visible]);

  // Fetch rooms on mount to show online count
  useEffect(() => {
    if (visible) {
      fetchRoomsForStats();
    }
  }, [visible]);

  const fetchRooms = async () => {
    setError('');

    try {
      const browserLanguage =
        navigator.language || navigator.userLanguage || 'en';
      const auth = getAuth();
      const headers = {
        'Content-Type': 'application/json',
      };

      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken();
          headers.Authorization = 'Bearer ' + token;
        } catch (tokenError) {
          console.warn('Failed to get auth token:', tokenError);
        }
      }

      const response = await fetch(
        API_URL + `rooms?language=${encodeURIComponent(browserLanguage)}`,
        {
          method: 'GET',
          headers,
        }
      );

      const result = await response.json();

      if (response.ok) {
        return result.rooms || [];
      } else {
        setError(result.message || 'Failed to load rooms');
        return [];
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError('Network error. Please try again.');
      return [];
    }
  };

  const fetchRoomsForStats = async () => {
    const fetchedRooms = await fetchRooms();
    if (fetchedRooms.length > 0) {
      // Calculate total online users across all accessible rooms
      const total = fetchedRooms
        .filter((room) => room.accessible !== false)
        .reduce((sum, room) => sum + (room.onlineUsers || 0), 0);
      
      setTotalOnline(total);
    }
  };

  const handleJoinChat = async () => {
    // Track conversion
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-946225425/-3x2CKuys_IBEJGCmcMD'
      });
    }

    setLoading(true);

    try {
      const fetchedRooms = await fetchRooms();

      if (fetchedRooms.length === 0) {
        setError('No rooms available. Please try again later.');
        setLoading(false);
        return;
      }

      // Find the most popular accessible room
      const accessibleRooms = fetchedRooms.filter(
        (room) => room.accessible !== false
      );

      if (accessibleRooms.length > 0) {
        // Select the room with the most online users
        const targetRoom = accessibleRooms.reduce((prev, current) =>
          current.onlineUsers > prev.onlineUsers ? current : prev
        );

        // Don't close the modal here - let the parent handle it
        // This keeps the loading state visible until the room is actually selected
        onRoomSelect(targetRoom);
        // Keep loading state active - parent will close the modal
      } else {
        setError(
          'No accessible rooms available. Please check your profile settings.'
        );
        setLoading(false);
      }
    } catch (err) {
      console.error('Error selecting room:', err);
      setError('Failed to join chat. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={() => {}}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <WelcomeSection>
            <ModernTitle>
              Welcome to <HighlightText>{getSiteName()}</HighlightText>
            </ModernTitle>
            <IntroText>
              Chill, anonymous chat room where you can express yourself freely.
            </IntroText>
          </WelcomeSection>

          {totalOnline !== null && (
            <OnlineCount>
              <Users />
              <span>
                {totalOnline} {totalOnline === 1 ? 'person' : 'people'} online now
              </span>
            </OnlineCount>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <AgeDisclaimer>
            You must be {MINIMUM_AGE}+ to continue. By proceeding, you confirm you meet this age requirement and agree to our{' '}
            <a href="/rules" target="_blank" rel="noopener noreferrer">
              community rules
            </a>.
          </AgeDisclaimer>

          <ButtonContainer>
            <JoinButton
              onClick={handleJoinChat}
              disabled={loading}
            >
              {loading ? (
                <LoadingText>Joining chat...</LoadingText>
              ) : (
                <>
                  <MessageCircle />
                  Join the Chat
                </>
              )}
            </JoinButton>
          </ButtonContainer>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default OnboardingModalChatScape;